/* eslint-disable react/jsx-props-no-spreading */
import {observer} from 'mobx-react-lite';
import {getPayload} from 'payload';

import config from '@payload-config';
import {useLivePreview} from 'Application/useCases/useLivePreview';
import {getLachenschmidFooterData} from 'PayloadUI/dataSelectors/getLachenschmidFooterData';
import {getLachenschmidHeaderData} from 'PayloadUI/dataSelectors/getLachenschmidHeaderData';
import {getLachenschmidPageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import {LachenschmidLayout} from 'UI/layouts/LachenschmidLayout';
import {BlockRenderer} from 'UI/modules/BlockRenderer';

import {hasDocuments, isPopulated} from 'Application/utils/typeGuards';

import type {GetStaticPaths, GetStaticProps} from 'next';
import type {Payload} from 'payload';
import type {PageData} from 'PayloadUI/dataSelectors/getLachenschmidPageData';
import type {ParsedUrlQuery} from 'querystring';
import type {NextSSGPageWithLayout} from 'types/global';
import type {Footer, Header} from 'types/payload-types';

interface PageProps {
    footerData: Footer;
    headerData: Header;
    pageData: PageData;
}

/**
 * The `Home` component is a Next.js page component.
 * It is used as a route and can be accessed throught the nextjs router.
 *
 * @param props          The component props.
 * @param props.pageData The page data containing the content to be rendered, typically structured JSON from PayloadCMS.
 * @returns A ReactNode representing the `Home` page.
 */
const Home: NextSSGPageWithLayout<typeof getStaticProps> = observer(({pageData}: PageProps) => {
    const page = useLivePreview<PageData>({
        dataKey: 'Content',
        depth: 2,
        initialData: pageData
    });

    return (
        <BlockRenderer
            content={page.blocks}
            listData={hasDocuments(page) ? page.documentsAndFolders : undefined}
            metaData={
                isPopulated(pageData.pageMetaData)
                    ? pageData.pageMetaData
                    : undefined
            }
        />
    );
});

/**
 * `getLayout` is a static property of the `Home` component that is a function designed to wrap the component with a specified layout.
 * It receives the router object, page properties, and the PageComponent and returns the PageComponent wrapped with the necessary layout and properties.
 * This property is essential for applying consistent layouts across different pages in the application.
 *
 * @param route         The Next.js route representing the current route of the application.
 * @param pageProps     The page properties object representing the properties of the current page.
 * @param PageComponent The PageComponent representing the current page.
 * @returns A ReactNode representing the PageComponent wrapped with the necessary layout and properties.
 *
 * @example
 * ```tsx
 * const LayoutWrappedComponent = Home.getLayout(router, pageProps, PageComponent);
 * ```
 */
Home.getLayout = (route, pageProps, PageComponent) => (
    <LachenschmidLayout
        footerData={pageProps.footerData}
        headerData={pageProps.headerData}
        pageMetaData={pageProps.pageData.pageMetaData}
        route={route}
    >
        <PageComponent {...pageProps} />
    </LachenschmidLayout>
);

/**
 * `getLayoutKey` is a static property of the `Home` component that is a function designed to return the layout key string.
 * It is used to determine if the layout changed between pages to apply transitions between different layouts.
 * This property is crucial for managing different layouts within the application and for providing a specific layout key for the `Home` component.
 *
 * @returns A string representing the layout key.
 *
 * @example
 * ```tsx
 * const layoutKey = Home.getLayoutKey();
 * ```
 */
Home.getLayoutKey = () => 'Lachenschmid';

export default Home;

/**
 * `getStaticPaths` is a Next.js function exported as a part of Static Generation, specifically used for dynamic routes.
 * It determines the paths that have to be rendered to HTML at build time when using `getStaticProps`.
 * This function is crucial for specifying which paths are available, allowing Next.js to statically generate the pages at build time.
 *
 * @returns An object with a `fallback` property set to `false`, indicating that any paths not returned by `getStaticPaths` will result in a 404 page, and a `paths` property set to an empty array, indicating that no paths are pre-rendered at build time.
 * @todo If its an dynamic route generate your paths here.
 */
export const getStaticPaths: GetStaticPaths = () => ({
    fallback: 'blocking',
    paths: []
});

/**
 * The `StaticParams` interface extends the `ParsedUrlQuery` interface and is designed to represent the structure of the parameters object, specifically for static paths.
 * It adds an additional properties to the base `ParsedUrlQuery` interface, allowing for the correct typing of these in the `getStaticProps` function.
 * This interface is crucial for ensuring the correct usage and structure of parameters in functions or components that deal with static paths, providing TypeScript type checking and IntelliSense support in IDEs.
 */
interface StaticParams extends ParsedUrlQuery {
    /**
     * The `id` property represents a unique identifier, usually in the form of a string.
     * It is a required property and must be provided when an object of type `StaticParams` is expected.
     * This property is crucial for uniquely identifying elements, components, or data within the application, especially in the context of static paths.
     */
    slug?: string[];
}

/**
 * `getStaticProps` is a Next.js function designed to fetch data at build time and pass it as props to the page.
 * It is used in the context of Static Generation and is crucial for pre-rendering pages with dynamic routes, allowing for optimized performance and SEO.
 * This function is essential for fetching the necessary data required by a page and providing it as props, ensuring the correct data is available during the rendering process.
 *
 * @param props                  The properties passed to the `getStaticProps` function.
 * @param props.revalidateReason The reason for revalidation, which can be 'on-demand', 'build', or 'stale'.
 * @param props.params           The parameters object representing the dynamic segments of the URL.
 * @param props.draftMode        A boolean indicating whether to fetch draft content or not. Defaults to `false`.
 * @returns A promise resolving to an object containing the `props` to be passed to the page component and a `revalidate` property set to `1`, indicating the time, in seconds, after which a page re-generation can occur.
 */
// eslint-disable-next-line max-lines-per-function
export const getStaticProps: GetStaticProps<PageProps, StaticParams> = async ({
    draftMode,
    params,
    revalidateReason
}) => {
    const {slug} = (await params) ?? {};
    let payload: Payload | undefined;

    if (revalidateReason !== 'build') {
        payload = await getPayload({config});
    }

    const headerData = await getLachenschmidHeaderData(revalidateReason, draftMode, payload);
    const footerData = await getLachenschmidFooterData(revalidateReason, draftMode, payload);
    const pageData = await getLachenschmidPageData(revalidateReason, slug, draftMode, payload);

    if (!pageData) {
        return {
            redirect: {
                destination: '/404',
                permanent: false
            }
        };
    }

    return Promise.resolve({
        props: {
            footerData,
            headerData,
            pageData
        }
    });
};